import * as React from 'react';
import { GenericChartComponent, getMouseCanvas, isDefined, noop } from 'react-financial-charts';
import { HoverTextNearMouse } from '@react-financial-charts/interactive/lib/components/index';
import { isHoverForInteractiveType, saveNodeType, terminate } from 'react-financial-charts';
import { EachText } from './Wrapper';
import { getValueFromOverride } from '@/utils/utils';
import { ChartShapesContext } from '../../../contexts/ChartShapesContext';
import { ShapeType } from '../../..';
import type {
  DragOverrideBase,
  DrawingSharedState,
  HoverTextConfig,
  RenderNode,
  MoreProps,
  TextRenderNode,
} from '../../../types/commontypes';
import { toTextRFCProps } from '@/utils/shapePropsToAppearance';
import { getActiveTemplateNode } from '@/utils/useActiveShapeProps';
import { defaultShapePropertyValues } from '@/utils/defaultShapePropertyValues';

// eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
const ChartComponent = GenericChartComponent as unknown as React.ComponentType<Record<string, unknown>>;

interface InteractiveTextProps {
  readonly onChoosePosition: (e: React.MouseEvent, newText: RenderNode[], moreProps: MoreProps) => void;
  readonly onDragComplete?: (e: React.MouseEvent, newTextList: RenderNode[], moreProps: MoreProps) => void;
  readonly onSelect?: (e: React.MouseEvent, interactives: RenderNode[], moreProps: MoreProps) => void;
  readonly defaultText: {
    readonly text: string;
    readonly selected?: boolean;
  };
  hoverText?: Partial<HoverTextConfig>;
  readonly textList: RenderNode[];
  readonly enabled: boolean;
  readonly hoverId: string | null;
  readonly onHover?: (hoveredId: string | number | null, moreProps?: MoreProps) => void;
  readonly onDoubleClick?: (e: React.MouseEvent, moreProps: MoreProps) => void;
  readonly sharedState: DrawingSharedState;
  readonly isAnyShapeEnabled?: boolean;
}

interface InteractiveTextDragOverride extends DragOverrideBase {
  position?: [number, number];
}

interface InteractiveTextState {
  current?: Record<string, unknown>;
  override?: InteractiveTextDragOverride;
}

export class InteractiveText extends React.Component<InteractiveTextProps, InteractiveTextState> {
  public static contextType = ChartShapesContext;
  declare context: React.ContextType<typeof ChartShapesContext>;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private static _cachedDefaultProps: Record<string, any> | null = null;

  static get defaultProps() {
    if (!InteractiveText._cachedDefaultProps) {
      InteractiveText._cachedDefaultProps = {
        onSelect: noop,
        defaultText: {
          text: 'Enter Text',
          selected: false,
        },
        hoverText: {
          ...HoverTextNearMouse.defaultProps,
          enable: true,
          bgHeight: 'auto',
          bgWidth: 'auto',
          text: 'Click to select object',
          selectedText: '',
        },
        textList: [],
      };
    }
    return InteractiveText._cachedDefaultProps;
  }

  private getSelectionState: (...args: unknown[]) => unknown;
  private saveNodeType: <T>(idx: number) => React.Ref<T>;

  private terminate: (...args: unknown[]) => unknown;

  public constructor(props: InteractiveTextProps) {
    super(props);

    this.terminate = terminate.bind(this);
    this.saveNodeType = saveNodeType.bind(this);
    this.getSelectionState = isHoverForInteractiveType('textList').bind(this);

    this.state = {};
  }

  public render() {
    const { textList, defaultText, hoverText, isAnyShapeEnabled, onDoubleClick, sharedState } = this.props;
    const { override } = this.state;
    return (
      <g>
        {textList.map((each, idx) => {
          if (each.hidden) return null;
          const defaultHoverText = InteractiveText.defaultProps.hoverText;
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
          const eachAppearance = toTextRFCProps(each as TextRenderNode);
          const props = {
            ...defaultText,
            ...each,
            ...eachAppearance,
            hoverText: {
              ...defaultHoverText,
              ...hoverText,
              // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
              ...((each as unknown as { hoverText?: Record<string, unknown> }).hoverText || {}),
            },
          };
          return (
            <EachText
              key={idx}
              ref={this.saveNodeType(idx)}
              index={idx}
              {...props}
              uuid={each.uuid}
              selected={(each as unknown as { selected?: boolean }).selected ?? false}
              position={getValueFromOverride(
                override,
                each.uuid,
                'position',
                (each as typeof each & { position?: [number, number] }).position!,
              )}
              onDrag={this.handleDrag}
              onDragComplete={this.handleDragComplete}
              onSelect={this.handleSelect}
              onDragStart={(uuid: string) => this.setState({ override: { uuid } })}
              onHover={this.handleHover}
              locked={each.locked ?? false}
              onDoubleClick={onDoubleClick}
              sharedState={sharedState}
              isAnyShapeEnabled={isAnyShapeEnabled}
            />
          );
        })}
        <ChartComponent onMouseDown={this.handleDraw} canvasToDraw={getMouseCanvas} drawOn={['mousemove', 'pan']} />;
      </g>
    );
  }

  private readonly handleHover = (hoveredId: string | number | null, moreProps?: MoreProps) => {
    const { onHover } = this.props;
    if (onHover) {
      onHover(hoveredId, moreProps);
    }
  };

  private readonly handleSelect = (e: React.MouseEvent, selectedUuid: string, moreProps: MoreProps) => {
    const { textList } = this.props;

    // selected is RFC's transient field, not RenderNode's — cast through Record to attach it
    const updatedList = textList.map(item => {
      const copy: Record<string, unknown> = { ...item };
      copy.selected = item.uuid === selectedUuid;
      return copy as unknown as RenderNode;
    });
    const { onSelect } = this.props;
    if (onSelect !== undefined) {
      onSelect(e, updatedList, moreProps);
    }
  };

  private readonly handleDraw = (e: React.MouseEvent, moreProps: MoreProps) => {
    const { enabled } = this.props;
    if (enabled && moreProps.chartId) {
      const {
        mouseXY: [, mouseY],
        chartConfig: { yScale },
        xAccessor,
        currentItem,
      } = moreProps;

      const { defaultText, onChoosePosition, textList } = this.props;

      if (onChoosePosition !== undefined) {
        const newTextList = textList.map(each => ({
          ...each,
          selected: false, // Deselect all previous texts
        }));

        const xyValue = [xAccessor(currentItem), yScale.invert(mouseY)];
        const targetChartId = moreProps.chartId;
        const shapeUuid = crypto.randomUUID();
        // selected is RFC's transient field — attach via cast, not part of RenderNode
        const lastProps = getActiveTemplateNode(ShapeType.TEXT);
        const defaults = defaultShapePropertyValues[ShapeType.TEXT];
        const initial = { ...defaults, ...lastProps };
        const newText = {
          ...initial,
          ...defaultText,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
          position: xyValue as [number, number],
          uuid: shapeUuid,
          type: ShapeType.TEXT,
          selected: true,
          hidden: false,
          createdAt: Date.now(),
          updatedAt: Date.now(),
          chartId: targetChartId,
        } as unknown as RenderNode;

        onChoosePosition(e, [...newTextList, newText], moreProps);
      }
    }
  };

  private readonly handleDragComplete = (e: React.MouseEvent, moreProps: MoreProps) => {
    const { hoverId } = this.props;
    const { override } = this.state;
    if (isDefined(override) && override.uuid === hoverId) {
      const { position } = override;
      if (!isDefined(position)) {
        return;
      }
      const { textList } = this.props;
      const newTextList = textList.map((each, _idx) => {
        const selected = each.uuid === override.uuid;
        return selected
          ? {
              ...each,
              position: position,
              selected: true,
              updatedAt: Date.now(),
            }
          : {
              ...each,
              selected: false,
            };
      });
      this.setState(
        {
          override: undefined,
        },
        () => {
          const { onDragComplete } = this.props;
          if (onDragComplete !== undefined) {
            onDragComplete(e, newTextList, moreProps);
          }
        },
      );
    }
  };

  private readonly handleDrag = (_: React.MouseEvent, uuid: string, position: [number, number]) => {
    const { hoverId } = this.props;
    const { override } = this.state;
    if (override) {
      if (override.uuid !== uuid || override.uuid !== hoverId) {
        return;
      }
    }
    this.setState({
      override: {
        uuid,
        position,
      },
    });
  };
}
